import {AriaStatus} from '@primer/react/experimental'
import {useState} from 'react'

import {HiddenSelectionBanner} from './components/HiddenSelectionBanner'
import {getBlankMessage, getResultsAnnouncement, ListMessage} from './components/ListMessage'
import {PartialResultsRow} from './components/PartialResultsRow'
import {PickerDialog} from './components/PickerDialog'
import {PickerList} from './components/PickerList'
import {ReposPickerHeaderContent} from './components/ReposPickerHeaderContent'
import {concatAndDedup} from './helper/concat-and-dedup'
import {useQueryRepositories} from './hooks/use-query-repositories'
import type {CommonDialogProps, SingleSelectProps} from './types'

type SingleSelectReposPickerDialogProps = SingleSelectProps & CommonDialogProps

export function SingleSelectReposPickerDialog({
  scope,
  selected,
  onSubmit,
  onDismiss,
  returnFocusRef,
}: SingleSelectReposPickerDialogProps) {
  const [query, setQuery] = useState('')

  const [selectedItem, setSelectedItem] = useState(selected)
  const [snapshotSelectedItem, setSnapshotSelectedItem] = useState(selectedItem)

  const results = useQueryRepositories({scope, query})

  const onApply = () => {
    onSubmit(selectedItem)
    onDismiss()
  }

  const fetchedItems = results.data?.repositories || []
  const visibleItems = concatAndDedup(snapshotSelectedItem ? [snapshotSelectedItem] : [], fetchedItems)
  const blankMessage = getBlankMessage(results, visibleItems)
  const visibleIds = new Set(visibleItems.map(item => item.id))
  const isHiddenSelectedItem = selectedItem && !visibleIds.has(selectedItem.id)

  const resultsMessage = getResultsAnnouncement({
    blankMessage,
    itemsCount: visibleItems.length,
    totalItemsCount: results.data?.repositoryCount || 0,
  })

  const onQueryChanged = (newQuery: string) => {
    setQuery(newQuery)
    setSnapshotSelectedItem(newQuery ? undefined : selectedItem)
  }

  return (
    <PickerDialog
      onClose={onDismiss}
      footerButtons={[
        {
          onClick: onDismiss,
          content: 'Cancel',
        },
        {
          buttonType: 'primary',
          onClick: onApply,
          content: 'Select',
        },
      ]}
      onSubmit={onApply}
      returnFocusRef={returnFocusRef}
      renderHeader={({initialFocusRef, dialogLabelId, dialogDescriptionId}) => (
        <div>
          <ReposPickerHeaderContent
            dialogTitle="Select a repository"
            dialogLabelId={dialogLabelId}
            dialogDescriptionId={dialogDescriptionId}
            inputRef={initialFocusRef}
            scope={scope}
            onDismiss={onDismiss}
            onQueryExecuted={onQueryChanged}
          />
          <AriaStatus announceOnShow className="sr-only">
            {resultsMessage}
          </AriaStatus>
        </div>
      )}
      renderBody={() => (
        <PickerDialog.Body>
          <HiddenSelectionBanner hiddenSelectedCount={isHiddenSelectedItem ? 1 : 0} />
          {blankMessage ? (
            <ListMessage>{blankMessage}</ListMessage>
          ) : (
            <PickerList
              mode="single"
              items={visibleItems}
              selectedItems={selectedItem ? [selectedItem] : []}
              setSelectedItems={items => setSelectedItem(items[0])}
              scopeType={scope.type}
            />
          )}
          <PartialResultsRow itemCount={visibleItems.length} totalCount={results.data?.repositoryCount || 0} />
        </PickerDialog.Body>
      )}
    />
  )
}

try{ SingleSelectReposPickerDialog.displayName ||= 'SingleSelectReposPickerDialog' } catch {}